import "./disputes.css";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { CustomBreadcrumb } from "../../../shared/components/custom-breadcrumb/custom-breadcrumb";
import { CustomTable } from "../../../shared/components/custom-table/custom-table";
import { SERVICES } from "../../../core/services/services";
import { CustomLoader } from "../../../shared/components/custom-loader/custom-loader";
import { CustomModal } from "../../../shared/components/custom-modal/custom-modal";
import { Toast } from "primereact/toast";
import { OverlayPanel } from "primereact/overlaypanel";
import { DisputeDetails } from "../../back-office/disputes/dispute-details";
import { DisputesFilter } from "../../back-office/disputes/disputes-filter";
import { HELPER } from "../../../shared/helper/helper";
import ErrorBoundary from "../../../shared/components/error-boundary/error-boundary";
import { DisputeTypes, UserTypes } from "../../../shared/constants";
import { Optional } from "../../../shared/components/optional/optional";

export function FrontOfficeDisputes(props) {
  const toast = useRef(null);
  const op = useRef(null);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [error, setError] = useState("");
  const [search, setSearch] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [range] = useState(5);
  const [emptyText, setEmptyText] = useState("");
  const [mobile, setMobile] = useState(false);
  const [currentModalIndex, setCurrentModalIndex] = useState(0);
  const [disputes, setDisputes] = useState([]);
  const [pageNo, setPageNo] = useState(0);
  const [searchParams, setSearchParams] = useState(null);
  const [selectedDisputeLogCode, setSelectedDisputeLogCode] = useState(null);

  const tableHeaders = [
    { label: "Log code", value: "logCode" },
    { label: "Account Number", value: "customerAccountNumber" },
    { label: "Mandate Reference", value: "mandateReference" },
    { label: "Transaction Status", value: "transactionStatus" },
    { label: "Dispute status", value: "status" },
    { label: "Resolution status", value: "resolutionStatus" },
    { label: "Transaction Date", value: "transactionTime" },
    { label: "Dispute Log Date", value: "loggedOn" },
    { label: "Actions", value: "actions" },
  ];

  const openAction = useCallback((e, action, isMobile) => {
    // eslint-disable-next-line default-case
    switch (action) {
      case "VIEW": {
        setSelectedDisputeLogCode(e?.logCode);
        setMobile(!!isMobile);
        setCurrentIndex(3);
        break;
      }
    }
  }, []);

  const handleDisputeResponse = useCallback(
    (data) => {
      const result = data?.result;
      if (!result.content.length) {
        setEmptyText("No disputes found ...");
      } else {
        let arr = [];
        setTotalItems(result?.totalElements);
        setTotalPages(result?.totalPages);
        result?.content.forEach((e) => {
          arr.push({
            ...e,
            actions: "CR",
            detailsFunction: openAction,
          });
        });
        setDisputes(arr);
      }
      setError(null);
      setCurrentIndex(1);
      setLoading(false);
    },
    [openAction]
  );

  const getDisputes = useCallback(
    (query) => {
      setCurrentIndex(0);
      setVisible(false);
      setLoading(true);
      setDisputes([]);
      if (query) {
        setSearch(true);
      }
      let params = {
        page: pageNo,
        size: 10,
        ...query,
        ...searchParams,
      };
      params = HELPER.TO_URL_STRING(params);
      const apiUrl = `/frontoffice/dispute/${
        props.disputeType ===
        DisputeTypes.Frontoffice.PaymentFacilitatorDisputes
          ? "payment-facilitator"
          : "bank"
      }`;
      SERVICES.MAKE_GET_REQUEST(apiUrl, params)
        // SERVICES.GET_SUPER_AGENT_DISPUTES(params)
        .then((data) => {
          handleDisputeResponse(data);
        })
        .catch((error) => {
          setError(HELPER.PROCESS_ERROR(error));
          setCurrentIndex(1);
          setLoading(false);
        });
    },
    [pageNo, props.disputeType, handleDisputeResponse, searchParams]
  );

  useEffect(() => {
    if (searchParams) {
      getDisputes(searchParams);
    } else {
      getDisputes();
    }
  }, [getDisputes, searchParams]);

  function goToPage(pageNo) {
    setPageNo(pageNo);
  }

  function onHide() {}

  function openModal(index, isMobile) {
    setCurrentModalIndex(index);
    if (isMobile) {
      setCurrentIndex(2);
    } else {
      setVisible(true);
    }
  }

  function saveSearchParams(params) {
    setPageNo(0);
    setSearchParams(params);
    // setVisible(false)
    // getDisputes(params)
  }

  function reload() {
    setLoading(true);
    setCurrentIndex(0);
    if (searchParams) {
      getDisputes(searchParams);
    } else {
      getDisputes();
    }
  }

  function reset() {
    setSearchParams(null);
    setLoading(true);
    setSearch(false);
    setCurrentIndex(0);
    getDisputes();
  }

  function closeModal(isReload) {
    if (visible) {
      setVisible(false);
    }
    if (currentIndex === 2) {
      setCurrentIndex(1);
    }

    if (isReload === true) {
      reload();
    }
  }

  const mobileModal = () => {
    // eslint-disable-next-line default-case
    switch (currentModalIndex) {
      case 1:
        return (
          <DisputesFilter
            disputeType={props.disputeType}
            searchFunction={saveSearchParams}
            closeModal={closeModal}
          />
        );
    }
  };

  const modalContent = () => {
    // eslint-disable-next-line default-case
    switch (currentModalIndex) {
      case 1:
        return (
          <DisputesFilter
            disputeType={props.disputeType}
            searchFunction={saveSearchParams}
            closeModal={closeModal}
          />
        );
    }
  };

  const disputeView = () => {
    // eslint-disable-next-line default-case
    switch (currentIndex) {
      case 0:
        return (
          <div className="loading-container">
            <CustomLoader loadingText="Loading disputes..." />
          </div>
        );
      case 1:
        return (
          <CustomTable
            isReload={true}
            totalPages={totalPages}
            totalItems={totalItems}
            currentPage={pageNo + 1}
            range={range}
            emptyText={emptyText}
            search={search}
            reload={reset}
            error={error}
            items={disputes}
            headers={tableHeaders}
            nextPage={() => setPageNo(pageNo + 1)}
            prevPage={() => setPageNo(pageNo - 1)}
            goToFirstPage={() => setPageNo(0)}
            goToLastPage={() => setPageNo(totalPages - 1)}
            goToPage={goToPage}
            hasColumnWithCustomWidth={true}
            customColumnWidth={15}
            customWidthColumnName="logCode"
          />
        );
      case 2:
        return (
          <div className="mobile-modal-container">
            <div className="custom-card">
              <div className="mobile-card-position">{mobileModal()}</div>
            </div>
          </div>
        );
      case 3:
        return (
          <DisputeDetails
            logCode={selectedDisputeLogCode}
            goBack={goBack}
            mobile={mobile}
            userType={UserTypes.Frontoffice}
          />
        );
    }
  };

  const filterView = () => {
    if (currentIndex === 1) {
      return (
        <button
          disabled={loading}
          onClick={() => openModal(1, false)}
          className="primary-button"
        >
          <i className="pi pi-filter" />
          <span className="hide-btn-text"> Filter</span>
        </button>
      );
    } else {
      return <div />;
    }
  };

  const viewMobileFilter = () => {
    if (currentIndex === 1) {
      return (
        <div className="floating-mobile-buttons add-cursor">
          <i
            onClick={(e) => op.current.toggle(e)}
            className="pi pi-ellipsis-v"
            style={{ fontSize: "1.5em", color: "#464DF2" }}
          />
          <OverlayPanel
            ref={op}
            id="overlay_panel"
            style={{ width: "100px" }}
            className="overlaypanel-demo"
          >
            <div className="p-mb-2 p-ml-1">
              <span
                onClick={() => openModal(1, true)}
                className="custom-over-flow-text"
              >
                <i className="pi pi-filter" /> Filter
              </span>
            </div>
          </OverlayPanel>
        </div>
      );
    } else {
      return <div />;
    }
  };

  function goBack(refresh) {
    if (refresh === true) {
      reload();
      return;
    }
    setCurrentIndex(1);
  }

  return (
    <ErrorBoundary>
      <div className="disputes">
        <Toast ref={toast} />
        <div>
          <CustomModal
            closeModal={closeModal}
            onHide={onHide}
            visible={visible}
            modalContent={modalContent}
          />
        </div>
        <Optional show={currentIndex !== 3}>
          <div className="page-title p-text-left">
            {props.disputeType ===
            DisputeTypes.Frontoffice.PaymentFacilitatorDisputes
              ? "Payment Facilitator"
              : "Bank"}{" "}
            Disputes
          </div>
          <div className="p-mt-2">
            <CustomBreadcrumb page="Manage Disputes" />
          </div>
        </Optional>

        <div className="floating-buttons desktop-scree">
          <div className="p-grid">
            <div className="p-col-7"></div>
            <div className="p-col-5">
              <div className="p-grid">
                <div className="p-col-6"></div>
                <div className="p-col-6">{filterView()}</div>
              </div>
            </div>
          </div>
        </div>
        <div className="mobile-scree dcir-hide">
          <div className="p-grid">
            <div className="p-col-9"></div>
            <div className="p-col-3">{viewMobileFilter()}</div>
          </div>
        </div>
        <div>{disputeView()}</div>
      </div>
    </ErrorBoundary>
  );
}
